import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { helperService } from "src/services/helper.service";
import { getCourseCategoryLabel } from "@eagerdog/constants";
import { IEvent, ICourse } from "@eagerdog/interfaces";

import Icon from "src/components/Icon/Icon";
import HorizontalScroll from "src/components/HorizontalScroll/HorizontalScroll";
import { Modal, useModal } from "src/components/Modal/Modal";
import EventForm from "src/components/StepForm/EventForm/EventForm";
import CreateCourse from "src/routes/Courses/CreateCourse/CreateCourse";

import styles from "./CalendarItem.module.scss";
import ShareModal from "src/components/ShareModal/ShareModal";

interface IProps {
    type: "event" | "course",
    item: any,
    isAdmin: boolean,
    clubNumber?: string,
    onEventCreate: () => void
}

const CalendarItem: React.FC<IProps> = (props) => {
    const { toggle, isShown } = useModal();
    const [modalContent, setModalContent] = useState<string>();

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: props.item.currency.toUpperCase() || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const getShowTypes = () => {
        let _showTypes: string[] = [];
    
        if (props.item) {
            for (let s in props.item.show_types) {
                if (!_showTypes.includes(props.item.show_types[s].show_type))
                _showTypes.push(props.item.show_types[s].show_type);
            }
        }
    
        return _showTypes;
    };

    const isPastEvent = useMemo(() => {
        if (props.item && props.item.end_date) {
            return moment(props.item.end_date).isBefore(moment(), 'day');
        }
        return false;
    }, [props.item]);

    const isEvent = () => {
        return props.item.sanctioning_club ? true : false;
    }

    const getModalContent = () => {
        switch (modalContent) {
            case "eventForm":
                return <EventForm
                    clubNumber={props?.clubNumber as string | undefined}
                    clubId={props.item.club_id} 
                    event={props.item}
                    onFinish={(e: IEvent) => {
                        toggle();
                        props.onEventCreate && props.onEventCreate()
                    }} 
                />;
            case "createCourse":
                return <CreateCourse
                    course={props.item}
                    onSubmit={(c: ICourse) => {
                        toggle(); 
                    }} 
                />;
            case "shareModal":
                return <ShareModal
                    eventTitle={props.item.title}
                    eventUrl={isEvent() ? window.location.origin + "/e/" + props.item.handle : window.location.origin + "/c/" + props.item.handle}
                    onClose={() => { toggle(); }}
                />;
        }

        return <>none</>;
    }

    const getFeeRange = () => {
        let min:number = -1;
        let max:number = -1;

        for (let st in props.item.show_types) {
            if (min === -1 || min > props.item.show_types[st].fee) {
                min = props.item.show_types[st].fee;
            }

            if (max === -1 || max < props.item.show_types[st].fee) {
                max = props.item.show_types[st].fee;
            }
        }

        if (min === max) {
            if (min === -1) {
                return <>No Price Set</>;
            } else {
                return <>{formatter.format(min)}</>;
            }
        }

        return <>{formatter.format(min)} - {formatter.format(max)}</>;   
    }

    useEffect(() => {
        if (modalContent !== undefined) {
            toggle();
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalContent]);


    return (
        <div className={props.isAdmin ? `${styles.CalendarItem} ${styles.admin}` : `${styles.CalendarItem}`}>
            <div className={styles.inner}>
                <div className={styles.left}>
                    <div className={styles.date}>
                        <div className={`${styles.status} ${styles.sanction}`}>{isEvent() ? props.item.sanctioning_club + " Event" : "Course"}</div>
                        <div className={styles.dateItem}>
                            <Icon className={styles.calendarIcon} type="calendar" />
                            <span>{props.item.start_date && moment(props.item.start_date).format("MMM Do")} - {props.item.end_date && moment(props.item.end_date).format("MMM Do, YYYY")}</span>
                        </div>
                        <div className={styles.dateItem}>
                            <Icon className={styles.calendarIcon} type="location" />
                            <span>{helperService.formatAddress(props.item.address)}</span>
                        </div>
                    </div>
                    <div className={styles.titleWrap}>
                        {isPastEvent && <div className={styles.statusWrap}>
                            <div className={`${styles.status} ${styles.past}`}>Finished</div>
                        </div>}
                        {!isPastEvent && <div className={styles.statusWrap}>
                            <div className={`${styles.status} ${styles[props.item.status]}`}>{helperService.getEventStatus(props.item.status)}</div>
                        </div>}
                        <div className={styles.title}>{props.item.title}<span>{props.item.fee ? formatter.format(props.item.fee) : getFeeRange()}</span></div>
                    </div>
                    {isEvent() && <div className={styles.showTypeWrap}>
                        {getShowTypes().length > 0 && <HorizontalScroll className={styles.showTypes}>
                            {getShowTypes().map((show: string, index: number) => {
                                return (<li key={index} className="show">{show}</li>);
                            })}
                        </HorizontalScroll>}
                        {getShowTypes().length === 0 && <HorizontalScroll className={styles.showTypes}>
                            <li className="show">No Show Types</li>
                        </HorizontalScroll>}
                    </div>}
                    {!isEvent() && props.item && props.item.schedule && <div className={styles.showTypeWrap}>
                        <ul>
                            <li>{props.item.schedule!.length} Sessions</li>
                            {props.item.category && <li className="show">{getCourseCategoryLabel(props.item.category)}</li>}
                        </ul>
                    </div>}
                </div>
                <div className={styles.right}>
                    <div className={styles.actions}>
                        <ul>
                            {props.isAdmin && <li onClick={() => {
                                if (isEvent()) {
                                    setModalContent("eventForm");
                                } else {
                                    setModalContent("createCourse");
                                }
                            }}><Icon className={styles.icon} type="edit"></Icon></li>}
                            <li onClick={() => {
                                setModalContent("shareModal");
                            }}><Icon className={styles.icon} type="share"></Icon></li>
                        </ul>
                    </div>
                    {props.item.is_full && !props.isAdmin && <div className={styles.full}>{isEvent() ? "Event Full" : "Course Full"}</div>}
                    {(!props.item.is_full || props.isAdmin) && (
                        <>
                            {isEvent() && props.item.premium_file_url && <a href={props.item.premium_file_url} className={styles.premiumLink} target="_blank" rel="noreferrer">View Premium</a>}
                            <Link to={isEvent() ? `/e/${props.item.handle}` : `/c/${props.item.handle}`} state={{ from: 'club' }}>{isEvent() ? "View Event" : "View Course"}</Link>
                        </>
                    )}
                </div>
            </div>
            <Modal
                className={modalContent}
                isShown={isShown}
                hide={() => {
                    toggle();
                    setModalContent(undefined);
                }}
                modalContent={getModalContent()}
            />
        </div>
    );
};

export default CalendarItem;
